import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RestrictionModal from '@/components/RestrictionModal';
import useLearningRestrictions from '@/hooks/useLearningRestrictions';
import useCourseAccess from '@/hooks/useCourseAccess';
import type { LearningRestrictionSettings } from '@/utils/learningRestrictions';

type LearningMenuItem = {
  title: string;
  description: string;
  icon: string;
  path: string;
  restrictionKey: keyof LearningRestrictionSettings;
};

const learningMenu: LearningMenuItem[] = [
  {
    title: 'クイズで知識をチェックする',
    description: '理解度を気軽にチェック',
    icon: 'ri-quill-pen-line',
    path: '/quiz/menu',
    restrictionKey: 'quizEnabled',
  },
  {
    title: '模擬試験を行う',
    description: '本番形式で実践練習',
    icon: 'ri-timer-flash-line',
    path: '/mockexam',
    restrictionKey: 'mockExamEnabled',
  },
  {
    title: 'テキストを見る',
    description: '学習資料を確認',
    icon: 'ri-book-2-line',
    path: '/viewtext',
    restrictionKey: 'viewTextEnabled',
  },
];

export default function LearningPage() {
  const navigate = useNavigate();
  const { settings, loading: restrictionsLoading } = useLearningRestrictions();
  const { hasEligibleCourse, loading: courseLoading } = useCourseAccess();
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

  const handleNavigate = (path: string, restrictionKey?: keyof LearningRestrictionSettings) => {
    if (restrictionKey && !settings[restrictionKey]) {
      setIsRestrictionOpen(true);
      return;
    }

    if ((path === '/quiz/menu' || path === '/mockexam' || path === '/viewtext') && !hasEligibleCourse) {
      alert('お申込みのコースではご利用いただけません。');
      return;
    }

    navigate(path);
  };

  if (restrictionsLoading || courseLoading) {
    return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center">
          <button
            onClick={() => navigate('/home')}
            className="w-10 h-10 flex items-center justify-center -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <h1 className="flex-1 text-center font-bold text-gray-800 pr-8 text-base">
            学習する
          </h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-5">
        {learningMenu.map((item) => (
          <button
            key={item.path}
            onClick={() => handleNavigate(item.path, item.restrictionKey)}
            className="w-full bg-white rounded-2xl shadow-sm p-5 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#F4B183]/30 flex items-center justify-center text-[#C05621] text-2xl">
              <i className={item.icon}></i>
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-gray-800">{item.title}</h2>
              <p className="text-xs text-gray-500 mt-1">{item.description}</p>
            </div>
            <i className="ri-arrow-right-s-line text-gray-400 text-xl"></i>
          </button>
        ))}
      </main>

      <RestrictionModal
        open={isRestrictionOpen}
        message={settings.message}
        onAction={() => setIsRestrictionOpen(false)}
      />
    </div>
  );
}
